import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { FileUploadModule } from 'primeng/fileupload';
import { NgxAimAdminPrimengModule } from '../../../ngx-aim-admin.primeng.module';
import { AppUploadEvent, GenericTimeSlot, SubCategory, UpdateSubCateogryDto } from '../../../../../models';
import { HttpErrorService, ShortTermRentalService, UtilsService } from '../../../../../services';
import { TrueFalseText } from '../../../../../types';
import { NgxAimAdminCategoryService } from '../../ngx-aim-admin-category.service';
import { NgxAimTimePickerComponent } from '../../../../time-picker/ngx-aim-time-picker.component';

interface STRSubCatSpecsFormControls {
  name: FormControl<string>;
  value: FormControl<string>;
}

@Component({
  selector: 'ngx-aim-admin-edit-sub-category',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FileUploadModule,
    NgxAimAdminPrimengModule,
    NgxAimTimePickerComponent,
  ],
  templateUrl: './ngx-aim-admin-edit-sub-category.component.html',
  styleUrls: ['./ngx-aim-admin-edit-sub-category.component.scss', '../../../admin.scss'],
})
export class NgxAimAdminEditSubCategoryComponent implements OnInit {
  entityService = inject(NgxAimAdminCategoryService);
  fb = inject(FormBuilder);
  config = inject(DynamicDialogConfig);
  dialogRef = inject(DynamicDialogRef);
  messageService = inject(MessageService);
  httpErrorService = inject(HttpErrorService);
  utils = inject(UtilsService);
  strService = inject(ShortTermRentalService);

  timeSlots: GenericTimeSlot[] = [];
  headers: any;
  dropDownOptions: TrueFalseText[] = ['True', 'False'];

  form = this.fb.group({
    SubNumber: this.fb.nonNullable.control<number>(0),
    Description: this.fb.nonNullable.control<string>({ value: '', disabled: true }),
    showOnMainNav: this.fb.nonNullable.control<TrueFalseText>('False'),
    thumbSrc: this.fb.control<string | null>(null),
    webTitle: this.fb.nonNullable.control<string>('', [Validators.required]),
    shortDescription: this.fb.nonNullable.control<string>(''),
    longDescription: this.fb.nonNullable.control<string>(''),
    strSpecs: this.fb.nonNullable.array<FormGroup<STRSubCatSpecsFormControls>>([]),
    taxOverride: this.fb.nonNullable.control<number>(0),
    startDateText: this.fb.nonNullable.control<string>(''),
    endDateText: this.fb.nonNullable.control<string>(''),
    isLodging: this.fb.nonNullable.control<boolean>(false),
    day0CheckInTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day0CheckOutTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day1CheckInTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day1CheckOutTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day2CheckInTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day2CheckOutTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day3CheckInTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day3CheckOutTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day4CheckInTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day4CheckOutTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day5CheckInTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day5CheckOutTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day6CheckInTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    day6CheckOutTimeSlot: this.fb.control<GenericTimeSlot | null>(null),
    taxOverride2: this.fb.nonNullable.control<number>(0),
    taxName: this.fb.nonNullable.control<string>(''),
    tax2Name: this.fb.nonNullable.control<string>(''),
  });

  isLoading = false;
  isSaving = false;

  ngOnInit() {
    this.getAllTimeSlots();
    if (this.config.data) {
      this.getEntity(this.config.data.CategoryNumber, this.config.data.SubNumber);
    }
  }

  getEntity(CategoryNumber: number, SubNumber: number) {
    this.isLoading = true;
    this.entityService.findOneSub(CategoryNumber, SubNumber).subscribe({
      next: (entity) => this.setFormData(entity),
      error: (err) => this.onError(err, 'Could not retrieve sub category')
    });
  }

  getAllTimeSlots() {
    this.strService.getAllTimeSlots().subscribe({
      next: (timeSlots) => this.timeSlots = timeSlots,
      error: (err) => this.httpErrorService.onHttpError(err)
    });
  }

  setFormData(entity: SubCategory) {
    this.strSpecs.clear();
    this.form.patchValue({
      ...entity,
      showOnMainNav: this.utils.convertBooleanToTrueFalseText(entity.showOnMainNav),
      taxOverride: entity.taxOverride * 100,
      day0CheckInTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day0CheckInTime),
      day0CheckOutTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day0CheckOutTime),
      day1CheckInTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day1CheckInTime),
      day1CheckOutTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day1CheckOutTime),
      day2CheckInTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day2CheckInTime),
      day2CheckOutTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day2CheckOutTime),
      day3CheckInTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day3CheckInTime),
      day3CheckOutTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day3CheckOutTime),
      day4CheckInTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day4CheckInTime),
      day4CheckOutTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day4CheckOutTime),
      day5CheckInTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day5CheckInTime),
      day5CheckOutTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day5CheckOutTime),
      day6CheckInTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day6CheckInTime),
      day6CheckOutTimeSlot: this.timeSlots.find((slot) => slot.time === entity.day6CheckOutTime),
      taxOverride2: entity.taxOverride2 * 100,
    });
    entity.strSpecs?.forEach((strSpec) => {
      this.addStrSpec(strSpec.name, strSpec.value);
    });

    this.headers = { CategoryNumber: this.config.data.CategoryNumber, SubNumber: entity.SubNumber };
    this.isLoading = false;
  }

  onSave() {
    this.isSaving = true;
    const dto: UpdateSubCateogryDto = {
      CategoryNumber: this.config.data.CategoryNumber,
      SubNumber: this.form.controls.SubNumber.value,
      showOnMainNav: this.utils.convertTrueFalseTextToBoolean(this.form.controls.showOnMainNav.value),
      webTitle: this.form.controls.webTitle.value,
      shortDescription: this.form.controls.shortDescription.value,
      longDescription: this.form.controls.longDescription.value,
      strSpecs: this.strSpecs.getRawValue(),
      taxOverride: +this.form.controls.taxOverride.value / 100,
      startDateText: this.form.controls.startDateText.value,
      endDateText: this.form.controls.endDateText.value,
      isLodging: this.form.controls.isLodging.value,
      day0CheckInTime: this.form.controls.day0CheckInTimeSlot.value?.time || '',
      day0CheckInDisplayTime: this.form.controls.day0CheckInTimeSlot.value?.displayTime || '',
      day0CheckOutTime: this.form.controls.day0CheckOutTimeSlot.value?.time || '',
      day0CheckOutDisplayTime: this.form.controls.day0CheckOutTimeSlot.value?.displayTime || '',
      day1CheckInTime: this.form.controls.day1CheckInTimeSlot.value?.time || '',
      day1CheckInDisplayTime: this.form.controls.day1CheckInTimeSlot.value?.displayTime || '',
      day1CheckOutTime: this.form.controls.day1CheckOutTimeSlot.value?.time || '',
      day1CheckOutDisplayTime: this.form.controls.day1CheckOutTimeSlot.value?.displayTime || '',
      day2CheckInTime: this.form.controls.day2CheckInTimeSlot.value?.time || '',
      day2CheckInDisplayTime: this.form.controls.day2CheckInTimeSlot.value?.displayTime || '',
      day2CheckOutTime: this.form.controls.day2CheckOutTimeSlot.value?.time || '',
      day2CheckOutDisplayTime: this.form.controls.day2CheckOutTimeSlot.value?.displayTime || '',
      day3CheckInTime: this.form.controls.day3CheckInTimeSlot.value?.time || '',
      day3CheckInDisplayTime: this.form.controls.day3CheckInTimeSlot.value?.displayTime || '',
      day3CheckOutTime: this.form.controls.day3CheckOutTimeSlot.value?.time || '',
      day3CheckOutDisplayTime: this.form.controls.day3CheckOutTimeSlot.value?.displayTime || '',
      day4CheckInTime: this.form.controls.day4CheckInTimeSlot.value?.time || '',
      day4CheckInDisplayTime: this.form.controls.day4CheckInTimeSlot.value?.displayTime || '',
      day4CheckOutTime: this.form.controls.day4CheckOutTimeSlot.value?.time || '',
      day4CheckOutDisplayTime: this.form.controls.day4CheckOutTimeSlot.value?.displayTime || '',
      day5CheckInTime: this.form.controls.day5CheckInTimeSlot.value?.time || '',
      day5CheckInDisplayTime: this.form.controls.day5CheckInTimeSlot.value?.displayTime || '',
      day5CheckOutTime: this.form.controls.day5CheckOutTimeSlot.value?.time || '',
      day5CheckOutDisplayTime: this.form.controls.day5CheckOutTimeSlot.value?.displayTime || '',
      day6CheckInTime: this.form.controls.day6CheckInTimeSlot.value?.time || '',
      day6CheckInDisplayTime: this.form.controls.day6CheckInTimeSlot.value?.displayTime || '',
      day6CheckOutTime: this.form.controls.day6CheckOutTimeSlot.value?.time || '',
      day6CheckOutDisplayTime: this.form.controls.day6CheckOutTimeSlot.value?.displayTime || '',
      taxOverride2: +this.form.controls.taxOverride2.value / 100,
      taxName: this.form.controls.taxName.value,
      tax2Name: this.form.controls.tax2Name.value,
    }

    this.entityService.saveSub(dto).subscribe({
      next: (entity) => this.onSuccess(entity),
      error: (err) => this.onError(err, 'Could not save sub category ')
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onError(err: any, message: string) {
    this.isSaving = false;
    this.isLoading = false;
    this.httpErrorService.onHttpError(err, message);
  }

  onSuccess(entity: SubCategory) {
    this.setFormData(entity);
    this.isSaving = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Sub category saved',
      key: 'app-toast'
    });
  }

  onUpload(event: AppUploadEvent) {
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Image Uploaded', key: 'app-toast' });
    this.form.patchValue({ thumbSrc: event.originalEvent.body.url });
  }

  onUploadError(event: any) {
    this.messageService.add({ severity: 'error', summary: 'Failed', detail: 'Image could not be uploaded', key: 'app-toast' });
  }

  addStrSpec(name: string = '', value: string = '') {
    const strSpec = this.fb.nonNullable.group<STRSubCatSpecsFormControls>({
      name: this.fb.nonNullable.control<string>(name, [Validators.required]),
      value: this.fb.nonNullable.control<string>(value, [Validators.required]),
    });

    this.strSpecs.push(strSpec);
  }

  removeStrSpec(index: number) {
    this.strSpecs.removeAt(index);
  }

  get strSpecs() {
    return this.form.controls.strSpecs
  }

  onTimeChange(timeSlot: GenericTimeSlot, type: 'CheckInTimeSlot' | 'CheckOutTimeSlot', dayNumber: '0' | '1' | '2' | '3' | '4' | '5' | '6') {
    this.form.controls[`day${dayNumber}${type}`].setValue(timeSlot);
  }
}
